import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';

export interface DropdownItem {
  label: string;
  icon?: ReactNode;
  to?: string;
  onSelect?: () => void;
  danger?: boolean;
  divider?: boolean;
}

export function Dropdown({ trigger, items, align = 'right', label }: { trigger: ReactNode; items: DropdownItem[]; align?: 'left' | 'right'; label: string }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => !root.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const itemCls = (danger?: boolean) =>
    cn('flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm hover:bg-muted', danger && 'text-danger');

  return (
    <div ref={root} className="relative">
      <button type="button" aria-label={label} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)} className="rounded-lg">
        {trigger}
      </button>
      {open && (
        <div role="menu" className={cn('anim-pop absolute z-50 mt-2 min-w-52 rounded-xl border border-border bg-card p-1.5 shadow-pop', align === 'right' ? 'right-0' : 'left-0')}>
          {items.map((it, i) => (
            <div key={i}>
              {it.divider && <div className="my-1 h-px bg-border" />}
              {it.to ? (
                <Link role="menuitem" to={it.to} className={itemCls(it.danger)} onClick={() => setOpen(false)}>
                  {it.icon}{it.label}
                </Link>
              ) : (
                <button role="menuitem" type="button" className={itemCls(it.danger)} onClick={() => { setOpen(false); it.onSelect?.(); }}>
                  {it.icon}{it.label}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
